import React, { useState, useMemo, useCallback } from 'react';
import { getLocale, useIntl } from 'umi';
import { Segment } from 'tea-component';
import moment from 'moment';
import classNames from 'classnames';
import TipContext from '@home/components/TipContext';
import { downloadExcel } from '@/pages/dashboard/utils/download/index';
import type { IColumnFormatConfig, ITabDataConfig } from '@/pages/dashboard/utils/download/_type';
import { getName } from '@home/utils/common';
import {
  mergeTechlandByGameTableData,
  type ITechlandByGameMergeRowConfig,
} from '@home/pages/studio/home/_utils';
import './index.scss';
import { LANGUAGE_EN } from '@home/_config/config';

const WEEKDAY_DOWNLOAD_FIELD = '_dl_weekday';

const WEEKDAY_I18N_KEYS = [
  'common_mon',
  'common_tues',
  'common_wed',
  'common_thur',
  'common_fri',
  'common_sat',
  'common_sun',
];

interface ITabConfig {
  value: string;
  labelEn: string;
  labelCn: string;
  componentMetrics: any[];
  /** 覆盖全局 dataKey，用于 By Game 等独立数据源 Tab */
  dataKey?: string;
  /** false 时该 Tab 不拼接 dateColumn（如 By Game） */
  includeDateColumn?: boolean;
}

interface IByGameMergeConfig {
  outputDataKey?: string;
  rows: ITechlandByGameMergeRowConfig[];
}

interface IPerformanceDataTableWrapProps {
  dataKey?: string;
  titleEn?: string;
  titleCn?: string;
  tabsConfig: ITabConfig[];
  dateColumn?: Record<string, unknown>;
  downloadFileNameEn?: string;
  downloadFileNameCn?: string;
  /** By Game：从多个 card_metrics dataKey 合并为表格行 */
  byGameMergeConfig?: IByGameMergeConfig;
  data?: Record<string, unknown>;
  changeData?: (key: string, value: unknown) => void;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const buildDownloadColumns = (
  dateColumn: Record<string, any> | undefined,
  metrics: any[],
  isEN: boolean,
  showWeekday?: boolean,
): IColumnFormatConfig[] => {
  const cols: IColumnFormatConfig[] = [];

  if (dateColumn) {
    cols.push({
      code: dateColumn.metricKey || 'date',
      name: getName(isEN, dateColumn.enName, dateColumn.cnName) || 'Date',
      format: '@',
      width: 150,
    });

    if (showWeekday) {
      cols.push({
        code: WEEKDAY_DOWNLOAD_FIELD,
        name: isEN ? 'Day of week' : '星期',
        format: '@',
        width: 100,
      });
    }

    const extras: any[] = dateColumn.downloadExtraColumns || [];
    extras.forEach((col: any) => {
      cols.push({
        code: col.code,
        name: getName(isEN, col.enName, col.cnName) || col.code,
        format: col.format || '@',
        width: col.width || 100,
      });
    });
  }

  metrics.forEach((metric) => {
    const name = getName(isEN, metric.enName, metric.cnName) || metric.metricKey;
    const dlFmt = metric.features?.downLoadFormat;
    cols.push({
      code: metric.metricKey,
      name,
      format: dlFmt || '@',
      width: 120,
    });
    const dodKey = metric.features?.cell?.changeKey;
    if (dodKey) {
      const dodFmt = metric.features?.cell?.dodDownloadFormat || '0.00%';
      cols.push({
        code: dodKey,
        name: `${name} DoD`,
        format: dodFmt,
        width: 100,
      });
    }
  });

  return cols;
};

const PerformanceDataTableWrap: React.FC<IPerformanceDataTableWrapProps> = (props) => {
  const {
    dataKey = 'performanceTableData',
    titleEn = '',
    titleCn = '',
    tabsConfig = [],
    dateColumn,
    downloadFileNameEn,
    downloadFileNameCn,
    byGameMergeConfig,
    data,
    changeData,
    children,
    className,
    style,
  } = props;

  const isEN = getLocale() === LANGUAGE_EN;
  const { formatMessage } = useIntl();
  const [activeTab, setActiveTab] = useState<string>(tabsConfig[0]?.value || '');

  const effectiveData = useMemo(() => {
    if (!byGameMergeConfig?.rows?.length) {
      return data;
    }
    const outputKey = byGameMergeConfig.outputDataKey || 'performanceByGameData';
    const merged = mergeTechlandByGameTableData(
      data,
      byGameMergeConfig.rows,
      data?.filter as { scope?: string; } | undefined,
    );
    return {
      ...(data || {}),
      [outputKey]: merged,
    };
  }, [data, byGameMergeConfig]);

  const showWeekday = dateColumn?.features?.cell?.showWeekday === true;
  const dateKey = dateColumn?.metricKey || 'date';
  const dateInputFormat = dateColumn?.features?.cell?.inputFormat || 'YYYYMMDD';

  const period: string | undefined = effectiveData?.filter?.date_type;

  const resolveMetricPeriodNames = useCallback(
    (metrics: any[]): any[] => {
      if (!period) return metrics;
      return metrics.map((m: any) => {
        const pn = m.periodNames?.[period];
        if (!pn) return m;
        return {
          ...m,
          enName: pn.enName ?? m.enName,
          cnName: pn.cnName ?? m.cnName,
        };
      });
    },
    [period],
  );

  const resolvedTabsConfig = useMemo(
    () => tabsConfig.map((tab) => ({
      ...tab,
      componentMetrics: resolveMetricPeriodNames(tab.componentMetrics),
    })),
    [tabsConfig, resolveMetricPeriodNames],
  );

  const activeTabConfig = useMemo(
    () => resolvedTabsConfig.find((t) => t.value === activeTab) || resolvedTabsConfig[0],
    [resolvedTabsConfig, activeTab],
  );

  const hasGameIconColumn = useMemo(
    () => (activeTabConfig?.componentMetrics || []).some(
      (metric: { features?: { cell?: { type?: string; }; }; }) =>
        metric?.features?.cell?.type === 'gameIcon',
    ),
    [activeTabConfig],
  );

  const activeDataKey = activeTabConfig?.dataKey || dataKey;
  const activeIncludeDateColumn = activeTabConfig?.includeDateColumn !== false;

  const segmentOptions = useMemo(
    () => resolvedTabsConfig.map((t) => ({
      text: getName(isEN, t.labelEn, t.labelCn) || t.value,
      value: t.value,
    })),
    [resolvedTabsConfig, isEN],
  );

  const isMonthly = useMemo(() => {
    const rows = effectiveData?.[activeDataKey]?.data;
    if (!Array.isArray(rows) || !rows.length) return false;
    return String(rows[0]?.date || '').length <= 7;
  }, [effectiveData, activeDataKey]);

  const resolvedDateColumn = useMemo(() => {
    if (!activeIncludeDateColumn || !dateColumn) return undefined;
    if (!isMonthly) return dateColumn;
    const { features, ...rest } = dateColumn;
    return rest;
  }, [dateColumn, isMonthly, activeIncludeDateColumn]);

  const componentMetrics = useMemo(() => {
    const metrics = activeTabConfig?.componentMetrics || [];
    return resolvedDateColumn ? [resolvedDateColumn, ...metrics] : metrics;
  }, [activeTabConfig, resolvedDateColumn]);

  const injectWeekday = useCallback((rows: any[]): any[] => {
    if (!showWeekday || isMonthly) return rows;
    return rows.map((row) => {
      const dateVal = row?.[dateKey];
      if (!dateVal) return row;
      const m = moment(String(dateVal), dateInputFormat);
      if (!m.isValid()) return row;
      const isoDay = m.isoWeekday();
      const label = formatMessage({ id: WEEKDAY_I18N_KEYS[isoDay - 1] });
      return { ...row, [WEEKDAY_DOWNLOAD_FIELD]: isEN ? `${label}.` : label };
    });
  }, [showWeekday, isMonthly, dateKey, dateInputFormat, formatMessage, isEN]);

  const handleDownload = useCallback(async () => {
    const tabs: ITabDataConfig[] = resolvedTabsConfig.map((tab) => {
      const tabDataKey = tab.dataKey || dataKey;
      const includeDate = tab.includeDateColumn !== false;
      const allData = effectiveData?.[tabDataKey]?.data || [];
      const downloadData = includeDate ? injectWeekday(allData) : allData;
      return {
        tabName: getName(isEN, tab.labelEn, tab.labelCn) || tab.value,
        dataSource: downloadData,
        columns: buildDownloadColumns(
          includeDate ? dateColumn : undefined,
          tab.componentMetrics,
          isEN,
          includeDate && showWeekday && !isMonthly,
        ),
      };
    });

    const hasAnyData = tabs.some((t) => Array.isArray(t.dataSource) && t.dataSource.length > 0);
    if (!hasAnyData) return;

    try {
      const fileName = getName(isEN, downloadFileNameEn, downloadFileNameCn)
        || getName(isEN, titleEn, titleCn)
        || 'Download';
      await downloadExcel({ fileName, tabs });
    } catch (error) {
      console.error('PerformanceDataTableWrap download failed:', error);
    }
  }, [
    effectiveData,
    dataKey,
    isEN,
    resolvedTabsConfig,
    dateColumn,
    downloadFileNameEn,
    downloadFileNameCn,
    titleEn,
    titleCn,
    showWeekday,
    isMonthly,
    injectWeekday,
  ]);

  const contentHeaderNode = useMemo(
    () =>
      segmentOptions.length > 1 ? (
        <Segment
          className={"dbh-studio-home-performance-table-wrap-segment"}
          value={activeTab}
          onChange={(val: string) => setActiveTab(val)}
          options={segmentOptions}
        />
      ) : null,
    [activeTab, segmentOptions],
  );

  const operationNode = useMemo(
    () => (
      <div className="dbd-common-table__operation-btn">
        <TipContext
          tipText={formatMessage({ id: 'dbl_common_download' })}
          type={{ default: 'download', hover: 'download' }}
          onClick={handleDownload}
        />
      </div>
    ),
    [handleDownload, formatMessage],
  );

  const metricsCardWrapProps = useMemo(
    () => ({
      titleEn,
      titleCn,
      contentHeader: contentHeaderNode,
      operation: operationNode,
      link: false,
    }),
    [titleEn, titleCn, contentHeaderNode, operationNode],
  );

  return (
    <div
      className={classNames(
        'dbh-studio-home-performance-table-wrap',
        {
          'dbh-studio-home-performance-table-wrap--has-game-icon': hasGameIconColumn,
        },
        className,
      )}
      style={style}
    >
      {React.Children.map(children, (child: any) => {
        if (!child) return null;
        const childConfig = child?.props?.config;
        const childConfigProps = childConfig?.props || child?.props || {};
        const childCommonBaseTableProps = childConfigProps?.commonBaseTableProps || {};
        const childMetricsCardWrapProps = childConfigProps?.metricsCardWrapProps || {};

        const mergedMetricsCardWrapProps = {
          ...childMetricsCardWrapProps,
          ...metricsCardWrapProps,
        };

        const mergedCommonBaseTableProps = {
          ...childCommonBaseTableProps,
          componentMetrics,
        };

        const nextChildConfig = childConfig
          ? {
            ...childConfig,
            props: {
              ...childConfigProps,
              dataKey: activeDataKey,
              metricsCardWrapProps: mergedMetricsCardWrapProps,
              commonBaseTableProps: mergedCommonBaseTableProps,
            },
          }
          : undefined;

        return React.cloneElement(child, {
          dataKey: activeDataKey,
          data: effectiveData,
          changeData,
          ...(nextChildConfig ? { config: nextChildConfig } : {}),
          ...(!nextChildConfig
            ? {
              metricsCardWrapProps: mergedMetricsCardWrapProps,
              commonBaseTableProps: mergedCommonBaseTableProps,
            }
            : {}),
        });
      })}
    </div>
  );
};

export default PerformanceDataTableWrap;
